import { assertUnreachable } from '@sgme/fp';

import type { LegsData, OptionLegProperties, PremiumCategory, PremiumType } from '@/store/option/state.ts';

export type GroupQuoteLeg = { leg: OptionLegProperties; data: LegsData | undefined };

/** Raw legacy net premium: sum Sell bids minus Buy asks in the selected category and currency.
 * Keep signed streamed values; do not normalize quotes, scale by notional or include manual overrides.
 * Convert the completed raw sum to display units with formatGroupPremiumValue.
 */
export function sumGroupPremium(legs: GroupQuoteLeg[], premiumType: PremiumType) {
  const { premiumCategory, premiumCurrency } = premiumType;
  const units = { PERCENT: 'Percent', PPS: 'Pps', AMOUNT: 'Amount', VOLATILITY: 'Amount' } as const;
  const unit = units[premiumCategory];
  return sumValues(
    legs.map(({ leg, data }) => {
      const premiumData = data?.premiumData;
      if (premiumData === undefined || leg.side === undefined) {
        return undefined;
      }
      const quoteSide = leg.side === 'Buy' ? 'ask' : 'bid';
      const value = premiumData[`${quoteSide}${unit}Ccy${premiumCurrency}`];
      if (!isFiniteNumber(value)) {
        return undefined;
      }
      // Legacy totals use the signed streamed side, without bid/ask normalization.
      return leg.side === 'Buy' ? -value : value;
    }),
  );
}

/** Convert a completed raw group sum to legacy display units, without scaling by notional.
 * Percent and PPS use legacy toFixed(4) rounding before locale formatting; amount/volatility stay unchanged.
 * PPS uses the full currency-pair precision, unlike the per-leg conversion.
 */
export function formatGroupPremiumValue(
  rawValue: number,
  premiumCategory: PremiumCategory,
  currencyPairPrecision: number,
) {
  switch (premiumCategory) {
    case 'PERCENT':
      return Number((rawValue * 100).toFixed(4));
    case 'PPS':
      return Number((rawValue * 10 ** currencyPairPrecision).toFixed(4));
    case 'AMOUNT':
    case 'VOLATILITY':
      return rawValue;
    default:
      return assertUnreachable(premiumCategory, `Unknown premium category: ${premiumCategory}`);
  }
}

/** Legacy commercial margin: sum Buy asks and Sell bids in markupCurrency, without side signs. */
export function sumGroupMarkup(legs: GroupQuoteLeg[], currency: PremiumType['premiumCurrency']) {
  return sumValues(
    legs.map(({ leg, data }) => {
      if (leg.side === undefined) {
        return undefined;
      }
      return leg.side === 'Buy'
        ? data?.marginData?.[`askMarkupCcy${currency}`]
        : data?.marginData?.[`bidMarkupCcy${currency}`];
    }),
  );
}

export function sumGroupGreek(legs: GroupQuoteLeg[], field: 'delta' | 'vega' | 'gamma' | 'theta') {
  return sumValues(legs.map(({ data }) => data?.greeks?.[`${field}Ccy1`]));
}

function sumValues(values: unknown[]): number | undefined {
  if (values.length === 0 || !values.every(isFiniteNumber)) {
    return undefined;
  }
  return values.reduce((total, value) => total + value, 0);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}
